/**
 * アイテム(docs/20 §5・docs/19 §3 ItemDef)。
 * 一覧: アイコン / 名前 / キャラ / 枠 / レア / ガチャ。絞り込み(キャラ・枠・レア・名前未設定・状態)とページング。
 * 編集: 名前(ja 必須)、枠、レアリティ(複数選択して一括変更)、効果、状態、合成プレビュー(pixel-layered)。
 * 素材のアップロードは今回作らない(R2 への書込 callable が未実装。取込ツールで行う)。
 */
import { useMemo, useState } from 'react';
import {
  ITEM_EFFECT_TYPES,
  ITEM_STATUSES,
  RARITIES,
  type CharacterDef,
  type ItemDef,
  type ItemEffect,
  type Rarity,
} from '@paltyping/content-schema';
import {
  EMPTY_ITEM_FILTER,
  bulkRarityChanges,
  fieldIssues,
  gachasByItem,
  isUnnamed,
  issuesFor,
  matchesItemFilter,
  pageCount,
  paginate,
  validateDrafts,
  type DraftEntry,
  type ItemFilter,
} from '../../services/catalog';
import { pixelAssetUrl } from '../../services/pixelCompose';
import {
  CatalogSummary,
  IssueList,
  NumberInput,
  ReadOnlyNote,
  SaveNotice,
  type CatalogStore,
  type SaveOutcome,
} from './common';
import { PixelPreview } from './PixelPreview';

export const ITEM_STATUS_LABELS: Readonly<Record<ItemDef['status'], string>> = {
  active: '有効',
  retired: '廃止',
};

export const EFFECT_LABELS: Readonly<Record<ItemEffect['type'], string>> = {
  gaugeDecayReduction: 'ゲージ減少を軽く',
  comboCapUp: 'コンボ上限アップ',
  missForgive: 'ミスを許す',
  coinBonus: 'コインボーナス',
};

interface Props {
  readonly store: CatalogStore;
  readonly editable: boolean;
  readonly contentBase: string | null;
}

export function ItemIcon({
  item,
  contentBase,
}: {
  readonly item: ItemDef;
  readonly contentBase: string | null;
}) {
  if (item.art.kind === 'emoji') return <span className="emoji-thumb">{item.art.emoji}</span>;
  if (contentBase === null || item.art.icon === '') return <span className="muted">—</span>;
  return (
    <img
      className="pixel-icon"
      src={pixelAssetUrl(contentBase, item.art.icon)}
      width={32}
      height={32}
      alt=""
      loading="lazy"
    />
  );
}

export function ItemsPanel({ store, editable, contentBase }: Props) {
  const { drafts } = store;
  const [filter, setFilter] = useState<ItemFilter>(EMPTY_ITEM_FILTER);
  const [page, setPage] = useState(0);
  const [selection, setSelection] = useState<ReadonlySet<string>>(new Set());
  const [bulkRarity, setBulkRarity] = useState<Rarity>('N');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<SaveOutcome | null>(null);

  const characters = useMemo(() => drafts.character.map((entry) => entry.data.def), [drafts]);
  const itemsById = useMemo(
    () => new Map(drafts.item.map((entry) => [entry.id, entry.data.def])),
    [drafts.item],
  );
  const gachaIndex = useMemo(
    () => gachasByItem(drafts.gacha.map((entry) => entry.data.def)),
    [drafts.gacha],
  );
  const summary = useMemo(() => validateDrafts(drafts), [drafts]);
  const filtered = useMemo(
    () => drafts.item.filter((entry) => matchesItemFilter(entry.data.def, filter)),
    [drafts.item, filter],
  );
  const pages = pageCount(filtered.length);
  const currentPage = Math.min(page, pages - 1);
  const visible = paginate(filtered, currentPage);
  const slotChoices = useMemo(() => {
    const character = characters.find((entry) => entry.id === filter.characterId);
    if (character) return character.slots.map((slot) => slot.id);
    return [...new Set(drafts.item.map((entry) => entry.data.def.slot))].sort();
  }, [characters, drafts.item, filter.characterId]);
  const editing = drafts.item.find((entry) => entry.id === editingId);

  function changeFilter(patch: Partial<ItemFilter>): void {
    setFilter((current) => ({ ...current, ...patch }));
    setPage(0);
  }

  function toggle(id: string): void {
    setSelection((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function applyBulkRarity(): Promise<void> {
    setBusy(true);
    setOutcome(null);
    const result = await store.save(bulkRarityChanges(drafts.item, selection, bulkRarity));
    setOutcome(result);
    if (result.ok) setSelection(new Set());
    setBusy(false);
  }

  async function saveItem(entry: DraftEntry<'item'>, def: ItemDef): Promise<void> {
    setBusy(true);
    setOutcome(null);
    setOutcome(await store.save([{ docType: 'item', id: def.id, data: { ...entry.data, def } }]));
    setBusy(false);
  }

  return (
    <div className="catalog-split catalog-split--wide">
      <div className="pane pane--list">
        <div className="toolbar">
          <h2 className="section-title">アイテム</h2>
          <CatalogSummary errors={summary.errors.length} warnings={summary.warnings.length} />
        </div>
        <div className="toolbar toolbar--wrap" aria-label="絞り込み">
          <select
            className="select select--inline"
            aria-label="キャラクターで絞り込み"
            value={filter.characterId}
            onChange={(event) => {
              changeFilter({ characterId: event.target.value, slot: '' });
            }}
          >
            <option value="">キャラ: すべて</option>
            {characters.map((character) => (
              <option key={character.id} value={character.id}>
                {character.id}
              </option>
            ))}
          </select>
          <select
            className="select select--inline"
            aria-label="枠で絞り込み"
            value={filter.slot}
            onChange={(event) => {
              changeFilter({ slot: event.target.value });
            }}
          >
            <option value="">枠: すべて</option>
            {slotChoices.map((slot) => (
              <option key={slot} value={slot}>
                {slot}
              </option>
            ))}
          </select>
          <select
            className="select select--inline"
            aria-label="レアで絞り込み"
            value={filter.rarity}
            onChange={(event) => {
              changeFilter({ rarity: event.target.value as ItemFilter['rarity'] });
            }}
          >
            <option value="">レア: すべて</option>
            {RARITIES.map((rarity) => (
              <option key={rarity} value={rarity}>
                {rarity}
              </option>
            ))}
          </select>
          <select
            className="select select--inline"
            aria-label="状態で絞り込み"
            value={filter.status}
            onChange={(event) => {
              changeFilter({ status: event.target.value as ItemFilter['status'] });
            }}
          >
            <option value="">状態: すべて</option>
            {ITEM_STATUSES.map((status) => (
              <option key={status} value={status}>
                {ITEM_STATUS_LABELS[status]}
              </option>
            ))}
          </select>
          <label className="check">
            <input
              type="checkbox"
              aria-label="名前未設定だけ"
              checked={filter.unnamedOnly}
              onChange={(event) => {
                changeFilter({ unnamedOnly: event.target.checked });
              }}
            />
            名前未設定だけ
          </label>
          <input
            className="input input--search"
            aria-label="ID・名前で検索"
            placeholder="ID・名前"
            value={filter.text}
            onChange={(event) => {
              changeFilter({ text: event.target.value });
            }}
          />
        </div>

        {editable && (
          <div className="toolbar toolbar--wrap" aria-label="一括変更">
            <span className="muted">選択 {selection.size} 件</span>
            <button
              className="button button--small"
              type="button"
              onClick={() => {
                setSelection(new Set(filtered.map((entry) => entry.id)));
              }}
            >
              絞り込み結果をすべて選択({filtered.length})
            </button>
            <button
              className="button button--small"
              type="button"
              disabled={selection.size === 0}
              onClick={() => {
                setSelection(new Set());
              }}
            >
              選択解除
            </button>
            <select
              className="select select--inline"
              aria-label="一括で変えるレアリティ"
              value={bulkRarity}
              onChange={(event) => {
                setBulkRarity(event.target.value as Rarity);
              }}
            >
              {RARITIES.map((rarity) => (
                <option key={rarity} value={rarity}>
                  {rarity}
                </option>
              ))}
            </select>
            <button
              className="button button--small button--primary"
              type="button"
              disabled={busy || selection.size === 0}
              onClick={() => void applyBulkRarity()}
            >
              選択したレアリティを一括変更
            </button>
          </div>
        )}

        <SaveNotice outcome={outcome} onReload={() => void store.reload()} />

        <table className="table" aria-label="アイテム一覧">
          <thead>
            <tr>
              {editable && <th aria-label="選択" />}
              <th>アイコン</th>
              <th>名前</th>
              <th>キャラ</th>
              <th>枠</th>
              <th>レア</th>
              <th>ガチャ</th>
              <th>状態</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((entry) => {
              const item = entry.data.def;
              return (
                <tr
                  key={entry.id}
                  className={entry.id === editingId ? 'row--active' : undefined}
                  onClick={() => {
                    setOutcome(null);
                    setEditingId(entry.id);
                  }}
                >
                  {editable && (
                    <td
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                    >
                      <input
                        type="checkbox"
                        aria-label={`${entry.id} を選択`}
                        checked={selection.has(entry.id)}
                        onChange={() => {
                          toggle(entry.id);
                        }}
                      />
                    </td>
                  )}
                  <td>
                    <ItemIcon item={item} contentBase={contentBase} />
                  </td>
                  <td>
                    {isUnnamed(item) ? <span className="muted">(未設定)</span> : item.name.ja}
                    <div className="mono muted">{entry.id}</div>
                  </td>
                  <td className="mono">{item.characterId}</td>
                  <td className="mono">
                    {item.slot}
                    {item.starter === true && <span className="tag">starter</span>}
                  </td>
                  <td>{item.rarity}</td>
                  <td className="mono">{(gachaIndex.get(entry.id) ?? []).join(', ') || '—'}</td>
                  <td>{ITEM_STATUS_LABELS[item.status] ?? item.status}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="toolbar" aria-label="ページ">
          <button
            className="button button--small"
            type="button"
            disabled={currentPage === 0}
            onClick={() => {
              setPage(currentPage - 1);
            }}
          >
            前へ
          </button>
          <span className="muted">
            {currentPage + 1} / {pages} ページ(全 {filtered.length} 件)
          </span>
          <button
            className="button button--small"
            type="button"
            disabled={currentPage >= pages - 1}
            onClick={() => {
              setPage(currentPage + 1);
            }}
          >
            次へ
          </button>
        </div>
      </div>

      <div className="pane">
        {editing ? (
          <ItemEditor
            key={`${editing.id}:${editing.revision}`}
            initial={editing.data.def}
            store={store}
            editable={editable}
            busy={busy}
            characters={characters}
            itemsById={itemsById}
            contentBase={contentBase}
            onSave={(def) => saveItem(editing, def)}
          />
        ) : (
          <p className="muted">一覧からアイテムを選んでください。</p>
        )}
      </div>
    </div>
  );
}

interface EditorProps {
  readonly initial: ItemDef;
  readonly store: CatalogStore;
  readonly editable: boolean;
  readonly busy: boolean;
  readonly characters: readonly CharacterDef[];
  readonly itemsById: ReadonlyMap<string, ItemDef>;
  readonly contentBase: string | null;
  readonly onSave: (def: ItemDef) => Promise<void>;
}

function ItemEditor({
  initial,
  store,
  editable,
  busy,
  characters,
  itemsById,
  contentBase,
  onSave,
}: EditorProps) {
  const [working, setWorking] = useState<ItemDef>(initial);
  const result = useMemo(
    () => validateDrafts(store.drafts, { items: [working] }),
    [store.drafts, working],
  );
  const issues = issuesFor(result, 'item', working.id);
  const character = characters.find((entry) => entry.id === working.characterId);
  const previewItems = useMemo(() => {
    const map = new Map(itemsById);
    map.set(working.id, working);
    return map;
  }, [itemsById, working]);
  const previewLook = useMemo(
    () => ({ ...(character?.defaultLook.equipped ?? {}), [working.slot]: working.id }),
    [character, working.slot, working.id],
  );

  function update(patch: Partial<ItemDef>): void {
    setWorking((current) => ({ ...current, ...patch }));
  }

  function updateEffect(index: number, patch: Partial<ItemEffect>): void {
    update({
      effects: working.effects.map((effect, i) => (i === index ? { ...effect, ...patch } : effect)),
    });
  }

  return (
    <form
      className="editor"
      aria-label="アイテムの編集"
      onSubmit={(event) => {
        event.preventDefault();
        void onSave(working);
      }}
    >
      <h3>
        アイテム <span className="mono">{working.id}</span>
      </h3>
      <ReadOnlyNote editable={editable} />
      <IssueList issues={issues} />
      <div className="meta-grid">
        <span className="meta-grid__key">キャラクター</span>
        <span className="mono">{working.characterId}</span>
        <span className="meta-grid__key">素材</span>
        <span className="mono">
          {working.art.kind === 'emoji' ? `絵文字 ${working.art.emoji}` : working.art.sheet}
        </span>
        <span className="meta-grid__key">starter</span>
        <span>{working.starter === true ? 'はい(全員が所持扱い)' : 'いいえ'}</span>
      </div>
      <p className="muted">素材の追加は取込ツールで行います。</p>
      <fieldset className="editor__fields" disabled={!editable || busy}>
        <label className="field">
          <span className="field__label">名前(ja 必須。ひらがな優先)</span>
          <input
            className="input"
            aria-label="名前"
            value={working.name.ja ?? ''}
            onChange={(event) => {
              update({ name: { ...working.name, ja: event.target.value } });
            }}
          />
          <IssueList issues={fieldIssues(issues, 'name')} />
        </label>
        <label className="field">
          <span className="field__label">枠</span>
          <select
            className="select"
            aria-label="枠"
            value={working.slot}
            onChange={(event) => {
              update({ slot: event.target.value });
            }}
          >
            {!(character?.slots ?? []).some((slot) => slot.id === working.slot) && (
              <option value={working.slot}>{working.slot}(このキャラクターに無い枠)</option>
            )}
            {(character?.slots ?? []).map((slot) => (
              <option key={slot.id} value={slot.id}>
                {slot.id}({slot.name.ja})
              </option>
            ))}
          </select>
          <IssueList issues={fieldIssues(issues, 'slot')} />
        </label>
        <label className="field">
          <span className="field__label">レアリティ</span>
          <select
            className="select"
            aria-label="レアリティ"
            value={working.rarity}
            onChange={(event) => {
              update({ rarity: event.target.value as Rarity });
            }}
          >
            {RARITIES.map((rarity) => (
              <option key={rarity} value={rarity}>
                {rarity}
              </option>
            ))}
          </select>
        </label>
        <div className="field">
          <span className="field__label">効果</span>
          {working.effects.length === 0 && <p className="muted">効果なし</p>}
          {working.effects.map((effect, index) => (
            <div className="toolbar" key={index}>
              <select
                className="select select--inline"
                aria-label={`効果 ${index + 1} の種類`}
                value={effect.type}
                onChange={(event) => {
                  updateEffect(index, { type: event.target.value as ItemEffect['type'] });
                }}
              >
                {ITEM_EFFECT_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {EFFECT_LABELS[type]}({type})
                  </option>
                ))}
              </select>
              <NumberInput
                label={`効果 ${index + 1} の値`}
                value={effect.value}
                onChange={(value) => {
                  updateEffect(index, { value });
                }}
              />
              <button
                className="button button--small"
                type="button"
                aria-label={`効果 ${index + 1} を削除`}
                onClick={() => {
                  update({ effects: working.effects.filter((_, i) => i !== index) });
                }}
              >
                削除
              </button>
            </div>
          ))}
          <button
            className="button button--small"
            type="button"
            onClick={() => {
              update({ effects: [...working.effects, { type: ITEM_EFFECT_TYPES[0], value: 0 }] });
            }}
          >
            効果を追加
          </button>
          <IssueList issues={fieldIssues(issues, 'effects')} />
        </div>
        <label className="field">
          <span className="field__label">状態</span>
          <select
            className="select"
            aria-label="状態"
            value={working.status}
            onChange={(event) => {
              update({ status: event.target.value as ItemDef['status'] });
            }}
          >
            {ITEM_STATUSES.map((status) => (
              <option key={status} value={status}>
                {ITEM_STATUS_LABELS[status]}
              </option>
            ))}
          </select>
        </label>
      </fieldset>
      {character?.rigType === 'pixel-layered' && (
        <section className="card" aria-label="合成プレビュー">
          <h3>合成プレビュー(初期の見た目にこのアイテムを着せた idle-0)</h3>
          <PixelPreview contentBase={contentBase} equipped={previewLook} itemsById={previewItems} />
        </section>
      )}
      {editable && (
        <div className="actions">
          <button className="button button--primary" type="submit" disabled={busy}>
            {busy ? '保存中…' : '保存'}
          </button>
        </div>
      )}
    </form>
  );
}
